import { generateCodeChallenge } from "auth/generateCodeChallenge";
import { generateCodeVerifier } from "auth/generateCodeVerifier";
import { getOauthTokens } from "queries/identity";
import Rollbar from "rollbar";
import { API_TOKEN_INVALID_ERROR, LOGOUT_ACTION } from "utils/constants";
import { rollbarConfig } from "utils/rollbarConfig";

export class JwtClient {
  #jwt = null;
  #authenticatedInApp = false;
  #navigate = null;
  #dispatch = null;
  #setAuthExpired = null;

  setJwt(jwt) {
    this.#jwt = jwt;
    this.onAuthenticationChange(this.isAuthenticated());
  }

  setNativeJwt(nativeJwt) {
    this.#jwt = nativeJwt;
  }

  setNavigate(navigate) {
    this.#navigate = navigate;
  }

  setDispatch(dispatch) {
    this.#dispatch = dispatch;
  }

  authenticatedInApp(authenticated) {
    this.#authenticatedInApp = authenticated;
  }

  setAuthExpired(setAuthExpired) {
    this.#setAuthExpired = setAuthExpired;
  }

  async authenticateWithIdentity() {
    if (this.#jwt) return;
    const codeVerifier = generateCodeVerifier(48);
    const state = generateCodeVerifier(8);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    window.sessionStorage.setItem("code_verifier", codeVerifier);
    window.location = `${process.env.REACT_APP_API_BASE_URL}/identity/oauth/authorize?redirect_uri=${process.env.REACT_APP_REDIRECT_URI}&client_id=${process.env.REACT_APP_DASHBOARD_CLIENT_ID}&response_type=code&code_challenge=${codeChallenge}&code_challenge_method=S256&state=${state}`;
  }

  async getTokenWithAuthorizationCode(code, codeVerifier) {
    const params = `?code=${code}&grant_type=authorization_code&redirect_uri=${process.env.REACT_APP_REDIRECT_URI}&client_id=${process.env.REACT_APP_DASHBOARD_CLIENT_ID}&code_verifier=${codeVerifier}`;

    this.getTokens(params).catch((error) => {
      const rollbar = new Rollbar(rollbarConfig);
      rollbar.error("Refresh token with Authorization Code failed", {
        errorMessage: error,
      });
      throw new Error("Refresh token with Authorization Code failed");
    });
  }

  async attemptLogin(refreshToken, navigateTo, redirectUrl) {
    const params = `?grant_type=refresh_token&refresh_token=${refreshToken}&client_id=${process.env.REACT_APP_DASHBOARD_CLIENT_ID}`;
    this.getTokens(params)
      .then(() => navigateTo(redirectUrl)) // Enables landing on original URL when user refreshes the browser
      .catch(() => this.clearJwt()); // if the refresh_token call returns an error, assume token is expired and clear it from localStorage.
  }

  /**
   * @param {{
   * path: string,
   * queryParams?: Object,
   * params?: Object,
   * fetchParams?: Object,
   * throwRollBar?: boolean
   * }} props
   * @returns {Promise<any>}
   */
  async apiCall({
    path,
    queryParams = {},
    params = {},
    fetchParams = {},
    throwRollBar = true, // Some APIs throw errors for 404, which can be valid response, e.g. payment_smoother customer_configuration
  }) {
    if (!this.#jwt && !this.#authenticatedInApp) {
      throw new Error("JWT not set");
    }

    const rollbar = new Rollbar(rollbarConfig);
    const url = new URL(`${process.env.REACT_APP_API_BASE_URL}${path}`);

    // Remove null and undefined values from being sent as query parameters
    const filteredQueryParams = Object.fromEntries(
      Object.entries(queryParams).filter(
        (entry) => entry[1] !== null && entry[1] !== undefined,
      ),
    );

    url.search = new URLSearchParams(filteredQueryParams).toString();
    const headers = {
      headers: {
        Authorization: `Bearer ${this.#jwt}`,
        ...params.headers,
      },
    };
    const apiParams = fetchParams || { method: "GET" };
    let fetchFailure;
    const response = await fetch(url, { ...apiParams, ...headers }).catch(
      (error) => (fetchFailure = error),
    );

    if (!response) {
      const apiCallDetails = {
        apiUrl: `${process.env.REACT_APP_API_BASE_URL}${path}`,
        requestDetails: {
          path,
          queryParams,
          params,
          fetchParams,
        },
      };
      /**
       * instead of loging error we just want the info
       * since this error happens often and bloat up the rollbar error
       * we still need to monitor this to find the cause
       */
      rollbar.info("API response is null/undefined", {
        apiCallDetails,
      });

      throw new Error(
        JSON.stringify("API call failure: API response returns undefined"),
      );
    }

    // The response technically returns a 401 but we cant access it due to CORs.
    // When this happens the fetch request outright fails though so that gets caught
    // and we assign the error value to fetchFailure.
    // This means we can use the presence of fetchFailure to determine if the request failed
    // due to an invalid JWT (which is what the combo of 401 and CORs infers).
    if (Boolean(fetchFailure) && !Boolean(response.status)) {
      this.#setAuthExpired(true);
      rollbar.error("API call failure: JWT expired");
      throw new Error("JWT expired");
    }

    if (!response.status || response.status === 401) {
      const { error } = await response.json();
      this.#setAuthExpired(error === API_TOKEN_INVALID_ERROR);
      await this.refreshAuthentication();
      return;
    }

    if (!response.ok && throwRollBar) {
      this.#setAuthExpired(false);
      const responseBody = await response.json();
      rollbar.error(`API call failure: ${JSON.stringify(responseBody)}`, {
        response,
        apiUrl: `${process.env.REACT_APP_API_BASE_URL}${path}`,
        requestDetails: {
          path,
          queryParams,
          params,
          fetchParams,
        },
      });
      throw new Error(JSON.stringify(responseBody));
    }

    this.#setAuthExpired(false);
    return response;
  }

  async refreshAuthentication() {
    if (this.#authenticatedInApp) {
      return;
    }
    const refreshToken = window.localStorage.getItem("refreshToken");
    const params = `?grant_type=refresh_token&refresh_token=${refreshToken}&client_id=${process.env.REACT_APP_DASHBOARD_CLIENT_ID}`;
    await this.getTokens(params).catch(() => this.clearJwt());
  }

  async getTokens(params) {
    const data = await getOauthTokens(params);
    this.setJwt(data.id_token);
    window.localStorage.setItem("refreshToken", data.refresh_token || null);
  }

  setOnAuthenticationChange(onAuthenticationChange) {
    this.onAuthenticationChange = onAuthenticationChange;
  }

  isAuthenticated() {
    return !!this.#jwt;
  }

  clearJwt() {
    window.localStorage.clear(); // remove refresh token and prevent attemptLogin call
    window.sessionStorage.removeItem("code_verifier");
    this.apiCall({
      path: `/identity/logout`,
      queryParams: { id_token: this.#jwt },
      fetchParams: {
        method: "POST",
      },
    })
      .then(() => {
        // As identity's authorizations#logout throws an error due to an invalid redirect, we never reach this block.
        this.#navigate("/welcome");
        this.setJwt(null);
        this.#dispatch({ type: LOGOUT_ACTION });
      })
      .catch(() => {
        // Identity expects an encoded redirect_uri, otherwise it attempts to redirect to users#sign_in in the identity API.
        // Encoding occurs through the FlickJwt gem,  which is unavailable to javascript apps.
        // The default redirect to users#sign_in throws a CORs error, which gets us to this catch block. The App is functioning in the same way; forcing an internal redirect after logging out.
        this.#navigate("/welcome");
        this.setJwt(null);
        this.#dispatch({ type: LOGOUT_ACTION });
        // redirect does not delete the JWT; so while a user is on the /welcome page, clicking 'log in' without refreshing will allow the user back into their account
      });
  }
}

export const jwtClient = new JwtClient();
